import React, { useState } from "react";
import styles from "./FieldConfigurator.module.css";
import Select, { components, SingleValue } from "react-select";
import {
  FieldConfiguratorProps,
  findInputOptionByType,
  findOptionByValue,
} from "./FieldConfigurator.utils";
import * as Icon from "react-bootstrap-icons";
import {
  HeaderSelectOption,
  SelectOption,
} from "../../CygnusFormBuilderToolbar.utils";
import { Form, Image, Modal } from "react-bootstrap";
import SelectOptionRenderer from "./components/SelectOptionRenderer/SelectOptionRenderer";
import DependentParentSelector from "./components/DependentParentSelector/DependentParentSelector";
import { FieldOption } from "../../../../../CygnusFormBuilder/CygnusFormBuilder.utils";
import ApiDropdownConfigurator from "./components/ApiDropdownConfigurator/ApiDropdownConfigurator";
import DateFormatControls from "./components/DateFormatControls/DateFormatControls";
import { CurrencyFormat, NameFormat } from "../../../../../InputFieldsController/InputFieldsController.utils";

/**
 * FieldConfigurator Component
 */
const FieldConfigurator: React.FC<FieldConfiguratorProps> = ({
  isFinal,
  fieldOptions,
  selectedField,
  updateField,
  removeField,
}) => {
  // Setting default INPUT select options
  const inputTypeSelectOptions = [
    {
      label: "Headings",
      options: [
        {
          value: "header",
          label: "Title",
          id: "header",
          logo: (
            <Icon.Fonts
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
      ],
    },
    {
      label: "Inputs",
      options: [
        {
          value: "text",
          label: "Text",
          id: "text",
          logo: (
            <Icon.TextParagraph
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "textarea",
          label: "Large Text",
          id: "textarea",
          logo: (
            <Icon.BodyText
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "password",
          label: "Password",
          id: "password",
          logo: (
            <Icon.Key
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "contact",
          label: "Contact",
          id: "contact",
          logo: (
            <Icon.PhoneVibrate
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "ssn",
          label: "SSN",
          id: "ssn",
          logo: (
            <Icon.Regex
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "ein",
          label: "EIN",
          id: "ein",
          logo: (
            <Icon.Regex
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "currency",
          label: "Currency",
          id: "currency",
          logo: (
            <Icon.CurrencyDollar
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "numberinput",
          label: "Number",
          id: "numberinput",
          logo: (
            <Icon.Icon123
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "checkbox",
          label: "Checkbox",
          id: "checkbox",
          logo: (
            <Icon.Check2Square
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "slider",
          label: "Toggle",
          id: "slider",
          logo: (
            <Icon.Toggles2
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "radio",
          label: "Radio",
          id: "radio",
          logo: (
            <Icon.UiRadios
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "select",
          label: "Dropdown",
          id: "select",
          logo: (
            <Icon.MenuButtonWide
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "date",
          label: "Date",
          id: "date",
          logo: (
            <Icon.Calendar2Date
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
        {
          value: "file",
          label: "File",
          id: "file",
          logo: (
            <Icon.FileEarmarkMedical
              style={{ fontSize: 20, color: "var(--theme1_primary)" }}
            />
          ),
        },
      ],
    },
  ];

  // Setting HEADER select options for TITLE input type
  const headerOptions: HeaderSelectOption[] = [
    {
      value: "H1",
      label: <p style={{ fontSize: 20, marginBottom: 0 }}>Heading 1</p>,
      id: "H1",
    },
    {
      value: "H2",
      label: <p style={{ fontSize: 18, marginBottom: 0 }}>Heading 2</p>,
      id: "H2",
    },
    {
      value: "H3",
      label: <p style={{ fontSize: 16, marginBottom: 0 }}>Heading 3</p>,
      id: "6",
    },
    {
      value: "H4",
      label: <p style={{ fontSize: 14, marginBottom: 0 }}>Heading 4</p>,
      id: "H4",
    },
    {
      value: "H5",
      label: <p style={{ fontSize: 12, marginBottom: 0 }}>Heading 5</p>,
      id: "H5",
    },
    {
      value: "H6",
      label: <p style={{ fontSize: 10, marginBottom: 0 }}>Heading 6</p>,
      id: "H6",
    },
    {
      value: "PARAGRAPH",
      label: <p style={{ fontSize: 10, marginBottom: 0 }}>Paragraph</p>,
      id: "PARAGRAPH",
    },
  ];

  const CURRENCY_FORMAT_OPTIONS: {
    label: string;
    value: CurrencyFormat;
  }[] = [
      { label: "Integer", value: "integer" },
      { label: "Decimal", value: "decimal" },
    ];


  const NAME_FORMAT_OPTIONS: { label: string; value: NameFormat }[] = [
    { label: "Original", value: "original" },
    { label: "LastName, FirstName", value: "last_first" },
    { label: "FirstName LastName", value: "first_last" },
  ];

  // put these near the top of FieldConfigurator.tsx
  const EVENT_OPTIONS = [
    { label: "Client Prefill", value: "client.prefill" },
    { label: "Custom…", value: "__custom__" },
  ] as const;
  const PRESET_KEYS: string[] = EVENT_OPTIONS.map((o) => o.value).filter(
    (v) => v !== "__custom__"
  );
  const isPresetKey = (key?: string) => !!key && PRESET_KEYS.includes(key);
  const toSelectValue = (key?: string) =>
    isPresetKey(key)
      ? EVENT_OPTIONS.find((o) => o.value === key)!
      : EVENT_OPTIONS.find((o) => o.value === "__custom__")!;

  const VALUE_RULE_MODE_OPTIONS = [
    { label: "Set static value", value: "set" },
    { label: "Compute dynamically", value: "compute" },
  ] as const;

  const VALUE_RULE_COMPUTE_OPTIONS = [
    { label: "Copy parent value", value: "copy_parent" },
    { label: "Prefix + parent value", value: "prefix_parent" },
  ] as const;

  // Dropdown option modal
  const [showOptionsModal, setShowOptionsModal] = useState(false);

  let selectedInputType = findInputOptionByType(
    inputTypeSelectOptions,
    selectedField?.type || "text"
  );

  let selectedHeaderType = findOptionByValue(
    headerOptions,
    selectedField?.headerSize || "H1"
  );

  // Custom react select option renderer
  const { Option, Control } = components;

  /**
   * Render option with logo and then title
   * @param {*} props: defined select props
   * @returns: custom select option
   */
  const IconOption = (props: any) => {
    return (
      <Option {...props}>
        {props.data.logo} &nbsp;&nbsp;&nbsp;&nbsp;
        {props.data.label}
      </Option>
    );
  };

  /**
   * Icon option render controller
   * @param {*} param0: custom select option params
   * @returns: custom selected option
   */
  const IconControl = ({ children, ...props }: any) => {
    return (
      <Control {...props}>
        &nbsp;&nbsp;&nbsp;&nbsp;
        {props.selectProps.value.logo} &nbsp;&nbsp;
        {children}
      </Control>
    );
  };

  const updateSelectedField = (event: SingleValue<SelectOption>) => {
    // Dynamically update configured input type based on selected label
    const fieldInputType = findInputOptionByType(
      inputTypeSelectOptions,
      event?.type || "text"
    );
    selectedInputType = fieldInputType;
    updateField({
      type: fieldInputType.value,
      format: selectedField?.format ?? "original",
    });
    /** update the parent component as well - since we need event.value to be string
     *  we need to check if event is not null and event has a value */
    if (event && event.value && event.id) {
      updateField({
        label: event.label,
        allfields_id: event.id,
        allfields_key: event.value || event.label,
        allfields_code: event.code,
        allfields_category: event.type,
        allOptions: event.Options,
        options: event.Options,
      });
    }
  };

  const isEmptyValue =
    selectedField.value === null ||
    selectedField.value === undefined ||
    (typeof selectedField.value === "string" && selectedField.value.trim() === "");

  const resetValue = (f: any) => {
    if (f.type === "checkbox" || f.type === "radio" || f.type === "slider") return "";
    return "";
  };

  return (
    <>
      {showOptionsModal && (
        <Modal
          show={showOptionsModal}
          onHide={() => setShowOptionsModal(false)}
          aria-labelledby="contained-modal-title-vcenter"
          backdrop="static" // Prevents closing the modal by clicking outside
          centered
          scrollable={false}
          className={styles.uploadFilesModal}
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>Edit Dropdown Options</Modal.Title>
          </Modal.Header>
          <Modal.Body className={styles.optionsModalBody}>
            <SelectOptionRenderer
              selectedField={selectedField}
              updateField={updateField}
            />
          </Modal.Body>
        </Modal>
      )}

      <label style={{ fontWeight: 500, fontSize: 8 }}>Field</label>
      <Select<FieldOption>
        options={fieldOptions}
        value={selectedField}
        onChange={(event) => {
          updateSelectedField(event);
        }}
        placeholder={"Select field"}
        aria-label="Default"
        className="custom-select "
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, zIndex: 9999 }), // optional but helps
        }}
      />

      <label style={{ fontWeight: 500, fontSize: 8 }}>Type</label>
      <Select
        options={inputTypeSelectOptions}
        value={selectedInputType}
        onChange={(event) => {
          const fieldInputType = findInputOptionByType(
            inputTypeSelectOptions,
            event?.type || "text"
          );
          selectedInputType = fieldInputType;
          /** update the parent component as well - since we need event.value to be string
           *  we need to check if event is not null and event has a value */
          if (event && event.value) {
            updateField({
              type: event.value,
              ...(event.value === "currency"
                ? { currencyFormat: selectedField.currencyFormat ?? "integer" }
                : {}),
            });
          }
        }}
        components={{ Control: IconControl, Option: IconOption }}
        placeholder={"Select Input Type"}
        aria-label="Default"
        className="custom-select "
        classNamePrefix="react-select"
        menuPortalTarget={document.body}
        styles={{
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({ ...base, zIndex: 9999 }), // optional but helps
        }}
      />

      {/* Custom label for input field */}
      <label style={{ fontWeight: 500, fontSize: 8 }}>Custom Label</label>
      <textarea
        rows={3}
        className={`form-control move-left custom-input ${styles.textareaContainer}`}
        id="largeText"
        aria-describedby="labelHelp"
        placeholder={"Enter your label here"}
        value={selectedField.label}
        onChange={(event) => {
          /** update the parent component - since we need event.target.value to be string
           *  we need to check if event is not null and event has a value */
          if (event && event.target.value) {
            updateField({ label: event.target.value });
          }
        }}
      />

      {selectedInputType.value === "currency" && (
        <>
          <label style={{ fontWeight: 500, fontSize: 8 }}>
            Currency Format
          </label>

          <Select
            options={CURRENCY_FORMAT_OPTIONS}
            value={CURRENCY_FORMAT_OPTIONS.find(
              (opt) =>
                opt.value === (selectedField.currencyFormat ?? "integer")
            )}
            onChange={(opt) =>
              updateField({
                currencyFormat: opt?.value ?? "integer",
              })
            }
            placeholder="Select currency format"
            className="custom-select"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </>
      )}

      {selectedField?.type === "date" && (
      <>
        <DateFormatControls
          key={selectedField.id}
          field={selectedField}
          onPatch={(patch) => {
            // split top-level fields from dateConfig so we can deep-merge dateConfig
            const { dateConfig: dcPatch, ...rest } = patch as any;

            const next: any = {
              ...rest,
            };

            if (typeof patch.showTimeSelect !== "undefined") {
              next.showTimeSelect = patch.showTimeSelect;
            }

            if (dcPatch) {
              next.dateConfig = {
                ...(selectedField.dateConfig || {}),
                ...dcPatch,
              };
            }

            updateField(next);
          }}
        />
        <Form.Check
          type="checkbox"
          label="Is DOB field"
          checked={!!selectedField.isDOB}
          onChange={(e) => updateField({ isDOB: e.target.checked })}
          className="mt-2"
        />
      </>
      )}

      {/* Render Title size dropdown if field type is Header */}
      {selectedInputType.value === "header" && (
        <>
          <label style={{ fontWeight: 500, fontSize: 8 }}>Header</label>
          <Select<HeaderSelectOption>
            options={headerOptions}
            value={selectedHeaderType}
            onChange={(event) => {
              const fieldHeaderType = findOptionByValue(
                headerOptions,
                event?.value || "H1"
              );
              selectedHeaderType = fieldHeaderType;
              /** update the parent component as well - since we need event.value to be string
               *  we need to check if event is not null and event has a value */
              if (event && event.value) {
                updateField({ headerSize: event.value });
              }
            }}
            placeholder={"Select Input Type"}
            aria-label="Default"
            className="custom-select "
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }), // optional but helps
            }}
          />
        </>
      )}

      {/* Render Title size dropdown if field type is Header */}
      {selectedInputType.value === "radio" && (
        <>
          <label style={{ fontWeight: 500, fontSize: 8 }}>Radio Group</label>
          <Form.Control
            size="sm"
            type="text"
            placeholder="Enter a unique ID"
            className="custom-input mb-2"
            value={selectedField.radiogroup ?? ""}
            onChange={(e) => updateField({ radiogroup: e.target.value })}
          />
        </>
      )}

      {/* Slider configuration */}
      {selectedInputType.value === "slider" && (
        <>
          {/* OFF label (ON uses the field's main label) */}
          <label style={{ fontWeight: 500, fontSize: 8 }}>Off label</label>
          <Form.Control
            size="sm"
            type="text"
            placeholder="e.g. No / Disabled / Existing Client"
            className="custom-input mb-2"
            value={selectedField.toggleOffLabel ?? ""}
            onChange={(e) => updateField({ toggleOffLabel: e.target.value })}
          />

          <label style={{ fontWeight: 500, fontSize: 8 }}>Label position</label>
          <Form.Select
            size="sm"
            className="custom-input mb-2"
            value={selectedField.labelPosition ?? "left"}
            onChange={(e) =>
              updateField({ labelPosition: e.target.value as any })
            }
          >
            <option value="left">Left of switch</option>
            <option value="right">Right of switch</option>
          </Form.Select>
        </>
      )}

      {/* Default Value - applicable to all fields */}
      {!isEmptyValue && (
        <button
          type="button"
          className={styles.resetBtn}
          onClick={() => {
            updateField({ value: resetValue(selectedField), selectedOption: undefined });
            setTimeout(() => console.log("after reset:", selectedField.value), 0);
          }}
        >
          <Icon.ArrowCounterclockwise style={{ marginRight: 6 }} />
          Reset value
        </button>
      )}


      <label style={{ fontWeight: 500, fontSize: 8 }}>Default Value</label>
      <Form.Control
        size="sm"
        type="text"
        placeholder='Enter default value (use "true"/"false" for checkbox, slider, radio)'
        className="custom-input mb-2"
        value={selectedField.defaultValue ?? ""}
        onChange={(e) => updateField({ defaultValue: e.target.value })}
      />

      {/* Render select options configurator if field type is Select */}
      <div>
        {selectedInputType.value === "select" && (
          <>
            <div
              className={styles.dropdownModalButton}
              onClick={() => setShowOptionsModal(true)}
            >
              Edit options{" "}
              <Icon.BoxArrowInUpRight className={styles.dropdownModalIcon} />
            </div>
            <Form.Check
              type="checkbox"
              id="allow-multiple-checkbox"
              label="Allow multiple selections"
              checked={!!selectedField.isMulti}
              onChange={(e) => updateField({ isMulti: e.target.checked })}
              style={{ marginTop: 8, fontSize: 11 }}
            />
          </>
        )}
      </div>

      {/* Dependent Parent Field Selector */}
      <DependentParentSelector
        selectedField={selectedField}
        fieldOptions={fieldOptions}
        onChange={(updates) => updateField(updates)}
      />

      {/* Dependent Parent Field Selector */}
      <ApiDropdownConfigurator
        field={selectedField}
        onChange={(updates) => updateField(updates)}
      />

      {selectedInputType.value === "select" && (
        <>
          <label style={{ fontWeight: 500, fontSize: 8, marginTop: 8 }}>
            Format
          </label>

          <Select
            options={NAME_FORMAT_OPTIONS}
            value={NAME_FORMAT_OPTIONS.find(
              (opt) => opt.value === (selectedField.format ?? "original")
            )}
            onChange={(opt) => updateField({ format: opt?.value })}
            placeholder="Select name format"
            className="custom-select"
            classNamePrefix="react-select"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
          />
        </>
      )}
      {/* --- Typeahead Search Configuration --- */}
      {selectedInputType.value === "select" && (
        <>
          <label className={`${styles.automationSubLabel} mb-2 mt-2`}>
            Typeahead Settings
          </label>

          {/* Toggle for enabling typeahead */}
          <Form.Check
            type="checkbox"
            label="Typeahead Search"
            checked={!!selectedField.typeahead}
            onChange={(e) => updateField({ typeahead: e.target.checked })}
          />

          {/* Show additional settings only when typeahead is enabled */}
          {selectedField.typeahead && (
            <>
              <label style={{ fontWeight: 500, fontSize: 10, marginTop: 8 }}>
                Min characters to trigger search
              </label>
              <Form.Control
                size="sm"
                type="number"
                placeholder="e.g. 3"
                className="custom-input mb-2"
                min={1}
                value={selectedField.typeaheadMinChars ?? 3}
                onChange={(e) =>
                  updateField({
                    typeaheadMinChars: parseInt(e.target.value, 10),
                  })
                }
              />

              <label style={{ fontWeight: 500, fontSize: 10, marginTop: 8 }}>
                Fetch limit per request
              </label>
              <Form.Control
                size="sm"
                type="number"
                placeholder="e.g. 30"
                className="custom-input mb-2"
                min={1}
                value={selectedField.fetchLimit ?? 30}
                onChange={(e) => {
                  updateField({ fetchLimit: parseInt(e.target.value, 10) });
                }}
              />

              <Form.Check
                type="checkbox"
                label="Include child clients"
                className="mt-2"
                checked={selectedField.ClientSearchExtraPayload?.OnlyParents === false}
                onChange={(e) =>
                  updateField({
                    ClientSearchExtraPayload: e.target.checked
                      ? { OnlyParents: false }
                      : undefined,
                  })
                }
              />
            </>
          )}
        </>
      )}

      {/* --- Automation --- */}
      <label style={{ fontWeight: 600, fontSize: 10, marginTop: 8 }}>
        Automation
      </label>

      <div className={styles.automationSection}>
        <>
          <label className={styles.automationSubLabel}>Event key</label>
          <Select<{ label: string; value: string }>
            className={`custom-select ${styles.automationSelect}`}
            classNamePrefix="react-select"
            placeholder="Select event"
            menuPortalTarget={document.body}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              menu: (base) => ({ ...base, zIndex: 9999 }),
            }}
            options={EVENT_OPTIONS as any}
            value={toSelectValue(selectedField.onChangeEventKey)}
            onChange={(opt) => {
              if (!opt) return;
              updateField({
                onChangeEventKey:
                  opt.value === "__custom__"
                    ? isPresetKey(selectedField.onChangeEventKey)
                      ? ""
                      : selectedField.onChangeEventKey ?? ""
                    : opt.value,
              });
            }}
          />
          <Form.Check
            type="checkbox"
            label="Emit on change"
            checked={!!selectedField.emitOnChange}
            onChange={(e) =>
              updateField({
                emitOnChange: e.target.checked,
                onChangeEventKey: e.target.checked
                  ? selectedField.onChangeEventKey ?? ""
                  : "",
              })
            }
          />
          {/* Trigger toggles */}
          <Form.Check
            type="checkbox"
            label="Emit on blur (Tab)"
            checked={!!selectedField.emitOnBlur}
            onChange={(e) =>
              updateField({
                emitOnBlur: e.target.checked,
                onBlurEventKey: e.target.checked
                  ? selectedField.onBlurEventKey ?? ""
                  : selectedField.onBlurEventKey, // keep value; won't be used unless enabled
              })
            }
          />
          <Form.Check
            type="checkbox"
            label="Emit on Enter"
            checked={!!selectedField.emitOnEnter}
            onChange={(e) =>
              updateField({
                emitOnEnter: e.target.checked,
                onEnterEventKey: e.target.checked
                  ? selectedField.onEnterEventKey ?? ""
                  : selectedField.onEnterEventKey,
              })
            }
          />
          <label style={{ fontWeight: 600, fontSize: 10, marginTop: 8 }}>
            Manual Emit Button
          </label>
          <div className={styles.automationSection}>
            <Form.Check
              type="checkbox"
              label="Show button beside label"
              checked={!!selectedField.emitOnButton}
              onChange={(e) => updateField({ emitOnButton: e.target.checked })}
            />

            {selectedField.emitOnButton && (
              <>
                <label className={styles.automationSubLabel}>
                  Button label
                </label>
                <Form.Control
                  size="sm"
                  type="text"
                  className={`custom-input ${styles.automationInput}`}
                  placeholder="Emit"
                  value={selectedField.emitButtonLabel ?? ""}
                  onChange={(e) =>
                    updateField({ emitButtonLabel: e.target.value })
                  }
                />

                <label className={styles.automationSubLabel}>
                  Event key (optional)
                </label>
                <Form.Control
                  size="sm"
                  type="text"
                  className={`custom-input ${styles.automationInput}`}
                  placeholder="Leave empty to use Automation → onChange event"
                  value={selectedField.emitButtonEventKey ?? ""}
                  onChange={(e) =>
                    updateField({ emitButtonEventKey: e.target.value })
                  }
                />
              </>
            )}
          </div>

          {/* Show text box only when Custom is selected */}
          {!isPresetKey(selectedField.onChangeEventKey) && (
            <Form.Control
              size="sm"
              type="text"
              placeholder="e.g. my.pipeline.trigger"
              className={`custom-input ${styles.automationInput}`}
              value={selectedField.onChangeEventKey ?? ""}
              onChange={(e) =>
                updateField({ onChangeEventKey: e.target.value })
              }
            />
          )}
        </>
      </div>

      {/* ---------------- Value Rule Configuration ---------------- */}
      <label style={{ fontWeight: 600, fontSize: 10, marginTop: 10 }}>
        Value Rule
      </label>

      <div className={styles.automationSection}>
        {/* Parent for rule (allfields_code) */}
        <label className={styles.automationSubLabel}>Rule parent</label>
        <Select
          options={fieldOptions
            .filter((f) => f.code && f.id !== selectedField.id)
            .map((f) => ({
              label: `${f.label}`,
              value: f.code!,
            }))}
          value={
            selectedField.valueRule?.dependsOn
              ? {
                label: selectedField.valueRule.dependsOn,
                value: selectedField.valueRule.dependsOn,
              }
              : null
          }
          onChange={(opt: any) => {
            updateField({
              valueRule: {
                ...(selectedField.valueRule || {}),
                dependsOn: opt?.value || "",
              },
            });
          }}
          placeholder="Select rule parent"
          className="custom-select"
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />

        {/* Trigger values */}
        <label className={styles.automationSubLabel} style={{ marginTop: 8 }}>
          Apply rule when parent value is
        </label>
        <Form.Control
          size="sm"
          type="text"
          className={`custom-input ${styles.automationInput}`}
          placeholder="Comma-separated values, e.g. A,B,true"
          value={(selectedField.valueRule?.whenParentValueIn ?? []).join(",")}
          onChange={(e) => {
            const list = (e.target.value || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);

            updateField({
              valueRule: {
                ...(selectedField.valueRule || {}),
                whenParentValueIn: list,
              },
            });
          }}
        />

        {/* Mode */}
        <label className={styles.automationSubLabel} style={{ marginTop: 8 }}>
          Rule behavior
        </label>

        <Select
          options={VALUE_RULE_MODE_OPTIONS as any}
          value={
            (selectedField.valueRule?.computeKey
              ? VALUE_RULE_MODE_OPTIONS.find((o) => o.value === "compute")
              : VALUE_RULE_MODE_OPTIONS.find((o) => o.value === "set")) as any
          }
          onChange={(opt: any) => {
            const mode = opt?.value ?? "set";

            // If switching modes, clear the other branch to avoid conflicting config
            updateField({
              valueRule: {
                ...(selectedField.valueRule || {}),
                ...(mode === "set"
                  ? { computeKey: undefined, computeArgs: undefined }
                  : { setValue: undefined }),
              },
            });
          }}
          className="custom-select"
          classNamePrefix="react-select"
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, zIndex: 9999 }),
          }}
        />

        {/* Set static value */}
        {!selectedField.valueRule?.computeKey && (
          <>
            <label className={styles.automationSubLabel} style={{ marginTop: 8 }}>
              Set value to
            </label>
            <Form.Control
              size="sm"
              type="text"
              className={`custom-input ${styles.automationInput}`}
              placeholder='Value to force (use "true"/"false" for boolean)'
              value={selectedField.valueRule?.setValue ?? ""}
              onChange={(e) =>
                updateField({
                  valueRule: {
                    ...(selectedField.valueRule || {}),
                    setValue: e.target.value,
                  },
                })
              }
            />
          </>
        )}

        {/* Compute */}
        {!!selectedField.valueRule?.computeKey && (
          <>
            <label className={styles.automationSubLabel} style={{ marginTop: 8 }}>
              Compute rule
            </label>

            <Select
              options={VALUE_RULE_COMPUTE_OPTIONS as any}
              value={
                selectedField.valueRule?.computeKey
                  ? VALUE_RULE_COMPUTE_OPTIONS.find(
                    (o) => o.value === selectedField.valueRule?.computeKey
                  )
                  : null
              }
              onChange={(opt: any) =>
                updateField({
                  valueRule: {
                    ...(selectedField.valueRule || {}),
                    computeKey: opt?.value || "",
                    computeArgs: selectedField.valueRule?.computeArgs ?? {},
                  },
                })
              }
              className="custom-select"
              classNamePrefix="react-select"
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />

            {selectedField.valueRule?.computeKey === "prefix_parent" && (
              <>
                <label className={styles.automationSubLabel} style={{ marginTop: 8 }}>
                  Prefix
                </label>
                <Form.Control
                  size="sm"
                  type="text"
                  className={`custom-input ${styles.automationInput}`}
                  placeholder="e.g. CLIENT-"
                  value={selectedField.valueRule?.computeArgs?.prefix ?? ""}
                  onChange={(e) =>
                    updateField({
                      valueRule: {
                        ...(selectedField.valueRule || {}),
                        computeArgs: {
                          ...(selectedField.valueRule?.computeArgs || {}),
                          prefix: e.target.value,
                        },
                      },
                    })
                  }
                />
              </>
            )}
          </>
        )}

        {/* elseClear */}
        <Form.Check
          style={{ marginTop: 10 }}
          type="checkbox"
          label="Clear child value when rule is not active"
          checked={!!selectedField.valueRule?.elseClear}
          onChange={(e) =>
            updateField({
              valueRule: {
                ...(selectedField.valueRule || {}),
                elseClear: e.target.checked,
              },
            })
          }
        />

        {/* overwrite behavior */}
        <Form.Check
          style={{ marginTop: 6 }}
          type="checkbox"
          label="Allow rule to overwrite existing value"
          checked={!!selectedField.valueAuto}
          onChange={(e) => updateField({ valueAuto: e.target.checked })}
        />
      </div>


      {/* Utility wrapper */}
      <div className={styles.headerUtilityWrapper}>
        {selectedField.show ? (
          <Icon.Eye
            className={styles.headerEyeIcon}
            onClick={() => updateField({ show: false })}
          />
        ) : (
          <Icon.EyeSlash
            className={styles.headerEyeSlashIcon}
            onClick={() => updateField({ show: true })}
          />
        )}

        {selectedField.readOnly ? (
          <Icon.LockFill
            title="Make field editable"
            className={`${styles.headerEyeSlashIcon} ${styles.activeIcon}`}
            onClick={() => updateField({ readOnly: false })}
          />
        ) : (
          <Icon.UnlockFill
            title="Make field read only"
            className={styles.headerEyeSlashIcon}
            onClick={() => updateField({ readOnly: true })}
          />
        )}

        <Icon.FileEarmarkBreak
          title="Toggle Page Break"
          className={styles.headerEyeSlashIcon}
          onClick={() => updateField({ pageBreak: !selectedField.pageBreak })}
        />

        <Icon.Stack
          title="Toggle Section Start"
          className={`${styles.headerEyeSlashIcon} ${selectedField.sectionStart ? styles.activeIcon : ""
            }`}
          onClick={() => {
            const isStarting = !selectedField.sectionStart;
            updateField({
              sectionStart: isStarting,
              sectionName: isStarting
                ? selectedField.sectionName ?? "Untitled Section"
                : undefined,
              sectionStyles: isStarting
                ? selectedField.sectionStyles ?? {}
                : undefined,
            });
          }}
        />

        <Icon.WindowSplit
          title="Toggle Section End"
          className={`${styles.headerEyeSlashIcon} ${selectedField.sectionEnd ? styles.activeIcon : ""
            }`}
          onClick={() => {
            updateField({
              sectionEnd: !selectedField.sectionEnd,
            });
          }}
        />

        <Icon.TextWrap
          className={styles.headerEyeSlashIcon}
          onClick={() => updateField({ breakLine: !selectedField.breakLine })}
        />

        {!isFinal ? (
          <div style={{ width: "fit-content" }}>
            <Icon.Trash
              className={styles.headerDeleteIcon}
              onClick={() => removeField(selectedField.id)}
            />
          </div>
        ) : (
          <></>
        )}
        {selectedField.source ? (
          <Image
            src={selectedField.source}
            style={{ width: 20, height: 20, borderRadius: 5 }}
          />
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default FieldConfigurator;
